import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import AlertBox from '../../Components/AlertBox';

const Subscription = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingSubscription, setViewingSubscription] = useState(null);
  const [showAlert, setShowAlert] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const topRef = useRef(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    managerLimit: 0,
    duration: 'monthly',
    price: 0,
    accessPermissions: {
      taskManagement: false,
      leadManagement: false,
      hrms: false,
      support: false,
      projectManagement: false,
    },
    userLimits: {
      taskManagement: 0,
      leadManagement: 0,
      hrms: 0,
      support: 0,
      projectManagement: 0,
    }
  });

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  // Helper function to get sample data for testing
  const getSampleSubscriptionData = () => [
    {
      _id: 'sample1',
      title: '7-Day Trial',
      userLimit: 5,
      managerLimit: 1,
      duration: '7-day',
      price: 0,
      access: {
        task: true,
        lead: true,
        hrms: false,
        support: false,
        projectManagement: true,
      },
    },
    {
      _id: 'sample2',
      title: '15-Day Pro Trial',
      userLimit: 10,
      managerLimit: 2,
      duration: '15-day',
      price: 0,
      access: {
        task: true,
        lead: true,
        hrms: true,
        support: true,
        projectManagement: true,
      },
    },
    {
      _id: 'sample3',
      title: 'Basic Plan',
      userLimit: 10,
      managerLimit: 2,
      duration: 'monthly',
      price: 29.99,
      access: {
        task: true,
        lead: false,
        hrms: true,
        support: false,
        projectManagement: false,
      },
    },
    {
      _id: 'sample4',
      title: 'Premium Plan',
      userLimit: 50,
      managerLimit: 10,
      duration: 'yearly',
      price: 299.99,
      access: {
        task: true,
        lead: true,
        hrms: true,
        support: true,
        projectManagement: true,
      },
    },
    {
      _id: 'sample5',
      title: '2-Year Enterprise',
      userLimit: 100,
      managerLimit: 10,
      duration: '2-year',
      price: 1999.99,
      access: {
        task: true,
        lead: true,
        hrms: true,
        support: true,
        projectManagement: true,
      },
    },
  ];

  // Centralized function to normalize plan data for backward compatibility
  const normalizePlan = (plan) => {
    const accessPermissions = {
      taskManagement: plan.accessPermissions?.taskManagement ?? (plan.access?.task || false),
      leadManagement: plan.accessPermissions?.leadManagement ?? (plan.access?.lead || false),
      hrms: plan.accessPermissions?.hrms ?? (plan.access?.hrms || false),
      support: plan.accessPermissions?.support ?? (plan.access?.support || false),
      projectManagement: plan.accessPermissions?.projectManagement ?? (plan.access?.projectManagement || false),
    };

    const userLimits = {
      taskManagement: plan.userLimits?.taskManagement ?? (plan.userLimit || 0),
      leadManagement: plan.userLimits?.leadManagement ?? (plan.userLimit || 0),
      hrms: plan.userLimits?.hrms ?? (plan.userLimit || 0),
      support: plan.userLimits?.support ?? (plan.userLimit || 0),
      projectManagement: plan.userLimits?.projectManagement ?? (plan.userLimit || 0),
    };

    return {
      ...plan,
      accessPermissions,
      userLimits,
    };
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    const token = localStorage.getItem('authToken');

    try {
      console.log("Attempting to fetch subscription plans...");

      // Try with authentication first
      let response;
      if (token) {
        try {
          response = await axios.get('https://crm-tkxn.onrender.com/api/subscription-plans/', {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (authError) {
          console.log("Authentication failed, trying without auth:", authError.response?.status);
          // If auth fails, try without authentication
          if (authError.response?.status === 401 || authError.response?.status === 403) {
            response = await axios.get('https://crm-tkxn.onrender.com/api/subscription-plans/');
          } else {
            throw authError;
          }
        }
      } else {
        // No token available, try without authentication
        response = await axios.get('https://crm-tkxn.onrender.com/api/subscription-plans/');
      }

      console.log("Fetched subscription plans:", response.data);

      // Handle different response formats
      let subscriptionData = [];
      if (Array.isArray(response.data)) {
        subscriptionData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        subscriptionData = response.data.data;
      } else if (response.data?.subscriptionPlans && Array.isArray(response.data.subscriptionPlans)) {
        subscriptionData = response.data.subscriptionPlans;
      } else if (response.data?.plans && Array.isArray(response.data.plans)) {
        subscriptionData = response.data.plans;
      }

      // Normalize and standardize plan data for backward compatibility
      const normalizedPlans = subscriptionData.map(normalizePlan);

      setSubscriptions(normalizedPlans);

      if (subscriptionData.length === 0) {
        console.log("No subscription plans found in response");
        // You can add sample data here for testing if needed
        // subscriptionData = getSampleSubscriptionData();
      } else {
        console.log(`Successfully loaded ${subscriptionData.length} subscription plans`);
      }

    } catch (err) {
      console.error("Error fetching subscription plans:", err.response?.data || err.message || err);

      // Set a more user-friendly error message
      if (err.response?.status === 404) {
        setError('Subscription plans endpoint not found. Please check the API configuration.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Unable to fetch subscription plans. The component will work with local data entry.');
      }

      setSubscriptions([]); // Ensure subscriptions is always an array even on error
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('accessPermissions.')) {
      const accessField = name.split('.')[1];
      setFormData((prevFormData) => ({
        ...prevFormData,
        accessPermissions: {
          ...prevFormData.accessPermissions,
          [accessField]: checked,
        },
      }));
    } else if (name.startsWith('userLimits.')) {
      const limitField = name.split('.')[1];
      setFormData((prevFormData) => ({
        ...prevFormData,
        userLimits: {
          ...prevFormData.userLimits,
          [limitField]: value === '' ? '' : parseInt(value) || 0,
        },
      }));
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleAddSubscription = async (e) => {
    e.preventDefault();

    // Validation: At least one permission must be enabled
    const isAnyPermissionEnabled = Object.values(formData.accessPermissions).some(enabled => enabled);
    if (!isAnyPermissionEnabled) {
      setShowAlert('At least one permission must be enabled to create a plan');
      return;
    }

    const token = localStorage.getItem('authToken');
    const payload = {
      title: formData.title,
      managerLimit: formData.managerLimit,
      duration: formData.duration,
      price: formData.price,
      accessPermissions: formData.accessPermissions,
      userLimits: formData.userLimits,
    };

    try {
      console.log("Sending create subscription plan request with payload:", payload);

      let response;
      if (token) {
        try {
          response = await axios.post(
            'https://crm-tkxn.onrender.com/api/subscription-plans/',
            payload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
        } catch (authError) {
          if (authError.response?.status === 401 || authError.response?.status === 403) {
            // Try without authentication
            response = await axios.post(
              'https://crm-tkxn.onrender.com/api/subscription-plans/',
              payload,
              {
                headers: {
                  'Content-Type': 'application/json',
                },
              }
            );
          } else {
            throw authError;
          }
        }
      } else {
        // No token, try without authentication
        response = await axios.post(
          'https://crm-tkxn.onrender.com/api/subscription-plans/',
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      }

      console.log("Subscription plan created successfully:", response.data);
      setShowAlert('✅ Subscription plan created successfully!');

      // Use the returned plan object to update the state directly
      const newPlan = normalizePlan(response.data.plan);
      setSubscriptions(prev => [...prev, newPlan]);

      resetForm();
    } catch (error) {
      console.error("Error creating subscription plan:", error.response?.data || error.message || error);

      if (error.response?.status === 401 || error.response?.status === 403) {
        setSuccessMessage('⚠️ Subscription plan data processed locally. Server authentication required for full persistence.');
        setTimeout(() => setSuccessMessage(''), 5000);

        // Add to local state for immediate feedback
        const newSubscription = {
          _id: Date.now().toString(),
          ...payload,
          createdAt: new Date().toISOString(),
        };
        setSubscriptions(prev => [...prev, newSubscription]);
        resetForm();
      } else {
        setShowAlert('Error creating subscription plan. Check console for details.');
      }
    }
  };

  const handleEditSubscription = (subscription) => {
    setIsEditing(true);
    setEditingSubscriptionId(subscription._id);

    // Normalize for edit form (handles old plan structures with full object initialization)
    const accessPermissions = {
      taskManagement: subscription.accessPermissions?.taskManagement ?? (subscription.access?.task || false),
      leadManagement: subscription.accessPermissions?.leadManagement ?? (subscription.access?.lead || false),
      hrms: subscription.accessPermissions?.hrms ?? (subscription.access?.hrms || false),
      support: subscription.accessPermissions?.support ?? (subscription.access?.support || false),
      projectManagement: subscription.accessPermissions?.projectManagement ?? (subscription.access?.projectManagement || false),
    };

    const userLimits = {
      taskManagement: subscription.userLimits?.taskManagement ?? (subscription.userLimit || 0),
      leadManagement: subscription.userLimits?.leadManagement ?? (subscription.userLimit || 0),
      hrms: subscription.userLimits?.hrms ?? (subscription.userLimit || 0),
      support: subscription.userLimits?.support ?? (subscription.userLimit || 0),
      projectManagement: subscription.userLimits?.projectManagement ?? (subscription.userLimit || 0),
    };

    setFormData({
      title: subscription.title || '',
      managerLimit: subscription.managerLimit || 0,
      duration: subscription.duration || 'monthly',
      price: subscription.price || 0,
      accessPermissions,
      userLimits,
    });
    setShowForm(true);
    setScrollTrigger(prev => prev + 1); // Force scroll trigger
  };

  // Scroll to top whenever the form is shown (either add or edit)
  useEffect(() => {
    if (showForm) {
      // Use requestAnimationFrame for better reliability
      requestAnimationFrame(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [showForm, scrollTrigger]);


  const handleUpdateSubscription = async (e) => {
    e.preventDefault();

    // Validation: At least one permission must be enabled
    const isAnyPermissionEnabled = Object.values(formData.accessPermissions).some(enabled => enabled);
    if (!isAnyPermissionEnabled) {
      setShowAlert('At least one permission must be enabled to update a plan');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setShowAlert('Access token not found. Please login.');
      return;
    }

    const payload = {
      title: formData.title,
      managerLimit: formData.managerLimit,
      duration: formData.duration,
      price: formData.price,
      accessPermissions: formData.accessPermissions,
      userLimits: formData.userLimits,
    };

    try {
      console.log("Sending update subscription plan request with payload:", payload);

      const response = await axios.put(
        `https://crm-tkxn.onrender.com/api/subscription-plans/${editingSubscriptionId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log("Subscription plan updated successfully:", response.data);
      setShowAlert('✅ Subscription plan updated successfully!');

      // Use the returned plan object to update the state directly
      const updatedPlan = normalizePlan(response.data.plan);
      setSubscriptions(prev => prev.map(plan =>
        plan._id === updatedPlan._id ? updatedPlan : plan
      ));

      resetForm();
    } catch (error) {
      console.error("Error updating subscription plan:", error.response?.data || error.message || error);
      setShowAlert('Error updating subscription plan. Check console for details.');
    }
  };

  const handleDeleteSubscription = (subscriptionId) => {
    setSubscriptionToDelete(subscriptionId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setShowAlert('Access token not found. Please login.');
      setShowDeleteConfirm(false);
      return;
    }

    try {
      console.log("Sending delete subscription plan request for ID:", subscriptionToDelete);

      await axios.delete(
        `https://crm-tkxn.onrender.com/api/subscription-plans/${subscriptionToDelete}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Subscription plan deleted successfully");
      setShowAlert('✅ Subscription plan deleted successfully!');
      fetchSubscriptions(); // Refresh the list
    } catch (error) {
      console.error("Error deleting subscription plan:", error.response?.data || error.message || error);
      setShowAlert('Error deleting subscription plan. Check console for details.');
    } finally {
      setShowDeleteConfirm(false);
      setSubscriptionToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSubscriptionToDelete(null);
  };

  const handleViewSubscription = (subscription) => {
    // Normalize for view modal with full object initialization
    const accessPermissions = {
      taskManagement: subscription.accessPermissions?.taskManagement ?? (subscription.access?.task || false),
      leadManagement: subscription.accessPermissions?.leadManagement ?? (subscription.access?.lead || false),
      hrms: subscription.accessPermissions?.hrms ?? (subscription.access?.hrms || false),
      support: subscription.accessPermissions?.support ?? (subscription.access?.support || false),
      projectManagement: subscription.accessPermissions?.projectManagement ?? (subscription.access?.projectManagement || false),
    };

    const userLimits = {
      taskManagement: subscription.userLimits?.taskManagement ?? (subscription.userLimit || 0),
      leadManagement: subscription.userLimits?.leadManagement ?? (subscription.userLimit || 0),
      hrms: subscription.userLimits?.hrms ?? (subscription.userLimit || 0),
      support: subscription.userLimits?.support || 0,
      projectManagement: subscription.userLimits?.projectManagement ?? (subscription.userLimit || 0),
    };

    const normalizedView = {
      ...subscription,
      accessPermissions,
      userLimits
    };
    setViewingSubscription(normalizedView);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingSubscription(null);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      managerLimit: 0,
      duration: 'monthly',
      price: 0,
      accessPermissions: {
        taskManagement: false,
        leadManagement: false,
        hrms: false,
        support: false,
        projectManagement: false,
      },
      userLimits: {
        taskManagement: 0,
        leadManagement: 0,
        hrms: 0,
        support: 0,
        projectManagement: 0,
      }
    });
    setShowForm(false);
    setIsEditing(false);
    setEditingSubscriptionId(null);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  const getDurationBadgeColor = (duration) => {
    switch (duration) {
      case '7-day':
        return 'bg-green-100 text-green-800';
      case '15-day':
        return 'bg-teal-100 text-teal-800';
      case 'monthly':
        return 'bg-blue-100 text-blue-800';
      case 'quarterly':
        return 'bg-purple-100 text-purple-800';
      case 'yearly':
        return 'bg-orange-100 text-orange-800';
      case '2-year':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDurationDisplayName = (duration) => {
    const durationMap = {
      '7-day': '7 Days',
      '15-day': '15 Days',
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'yearly': 'Yearly',
      '2-year': '2 Years'
    };
    return durationMap[duration] || duration;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  if (error && subscriptions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium mb-2">{error}</p>
            <p className="text-sm text-gray-600 mb-4">API Endpoint: /api/subscription-plans/</p>
            <div className="space-y-2">
              <button
                onClick={fetchSubscriptions}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                „ Retry Fetching Data
              </button>
              <button
                onClick={() => {
                  setSubscriptions(getSampleSubscriptionData());
                  setError(null);
                }}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                📋 Load Sample Data
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div ref={topRef} className="max-w-7xl mx-auto"></div>
      <div className="max-w-7xl mx-auto">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Subscription Plans Management</h1>
            <p className="text-gray-600 mt-2">Manage all subscription plans in your system</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4 md:mt-0">
            <button
              onClick={fetchSubscriptions}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Data
            </button>
            <button
              onClick={() => {
                if (showForm) resetForm();
                else setShowForm(true);
              }}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
              {showForm ? 'Cancel' : 'Add Subscription Plan'}
            </button>
          </div>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">{isEditing ? 'Edit Subscription Plan' : 'Add New Subscription Plan'}</h2>
            <form onSubmit={isEditing ? handleUpdateSubscription : handleAddSubscription} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., Basic Plan, Premium Plan"
                  />
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Manager Limit</label>
                  <input
                    type="number"
                    name="managerLimit"
                    value={formData.managerLimit}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Maximum number of managers"
                  />
                </div> */}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="7-day">7 Days</option>
                    <option value="15-day">15 Days</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                    <option value="2-year">2 Years</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Plan price in INR"
                  />
                </div>
              </div>

              {/* Access Permissions & User Limits */}
              <div className="md:col-span-2 border-t pt-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">Access Permissions & User Limits</h3>
                <div className="space-y-4">
                  {[
                    { key: 'taskManagement', label: 'Task Management' },
                    { key: 'leadManagement', label: 'Lead Management' },
                    { key: 'hrms', label: 'HRMS' },
                    { key: 'support', label: 'Support' },
                    { key: 'projectManagement', label: 'Project Management' }
                  ].map(permission => (
                    <div key={permission.key} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <input
                          type="checkbox"
                          name={`accessPermissions.${permission.key}`}
                          checked={formData.accessPermissions[permission.key]}
                          onChange={handleInputChange}
                          className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                        />
                        <label className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                          {permission.label}
                        </label>
                      </div>

                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-gray-500 font-medium">User Limit:</span>
                        <div className="relative">
                          <input
                            type="number"
                            name={`userLimits.${permission.key}`}
                            value={formData.userLimits[permission.key]}
                            onChange={handleInputChange}
                            min="0"
                            disabled={!formData.accessPermissions[permission.key]}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  {isEditing ? 'Update Subscription Plan' : 'Create Subscription Plan'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Subscription Plans List</h2>
            <p className="text-gray-600 text-sm">{Array.isArray(subscriptions) ? subscriptions.length : 0} subscription plans found</p>
          </div>

          {!Array.isArray(subscriptions) || subscriptions.length === 0 ? (
            <div className="p-12 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-4 text-gray-600">No subscription plans found. Add your first subscription plan to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan Title</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions & Limits</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(subscriptions) && subscriptions.map((subscription, index) => (
                    <tr key={subscription._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{subscription.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDurationBadgeColor(subscription.duration)}`}>
                          {getDurationDisplayName(subscription.duration)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">{formatPrice(subscription.price)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-wrap gap-1">
                          {subscription.accessPermissions?.taskManagement && <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Task ({subscription.userLimits?.taskManagement || 0})</span>}
                          {subscription.accessPermissions?.leadManagement && <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Lead ({subscription.userLimits?.leadManagement || 0})</span>}
                          {subscription.accessPermissions?.hrms && <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">HRMS ({subscription.userLimits?.hrms || 0})</span>}
                          {subscription.accessPermissions?.support && <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">Support ({subscription.userLimits?.support || 0})</span>}
                          {subscription.accessPermissions?.projectManagement && <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">PM ({subscription.userLimits?.projectManagement || 0})</span>}
                          {!subscription.accessPermissions?.taskManagement &&
                            !subscription.accessPermissions?.leadManagement &&
                            !subscription.accessPermissions?.hrms &&
                            !subscription.accessPermissions?.support &&
                            !subscription.accessPermissions?.projectManagement && (
                              <span className="text-xs text-gray-400 italic">No Permissions Set</span>
                            )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewSubscription(subscription)}
                          className="text-blue-600 hover:text-blue-900 mr-3 transition-colors duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                        <button
                          onClick={() => handleEditSubscription(subscription)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3 transition-colors duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteSubscription(subscription._id)}
                          className="text-red-600 hover:text-red-900 transition-colors duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* View Modal */}
        {showViewModal && viewingSubscription && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[5000]">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                {/* Modal Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-800">Subscription Plan Details</h3>
                  <button
                    onClick={closeViewModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Content */}
                <div className="mt-6 space-y-6">
                  {/* Basic Information & Permissions Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-800 mb-3">Basic Information</h4>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Plan Title:</span>
                          <p className="text-lg font-bold text-gray-900">{viewingSubscription.title}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Duration:</span>
                          <span className={`ml-2 px-3 py-1 text-sm font-medium rounded-full ${getDurationBadgeColor(viewingSubscription.duration)}`}>
                            {getDurationDisplayName(viewingSubscription.duration)}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Price:</span>
                          <p className="text-2xl font-bold text-green-600">{formatPrice(viewingSubscription.price)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Access Permissions & Limits */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">Access Permissions & User Limits</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                          { key: 'taskManagement', label: 'Task Management' },
                          { key: 'leadManagement', label: 'Lead Management' },
                          { key: 'hrms', label: 'HRMS' },
                          { key: 'support', label: 'Support' },
                          { key: 'projectManagement', label: 'Project Management' }
                        ].map(permission => (
                          <div key={permission.key} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md">
                            <div className="flex items-center space-x-3">
                              <div className={`w-3 h-3 rounded-full ${viewingSubscription.accessPermissions?.[permission.key] ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <span className={`text-sm font-medium ${viewingSubscription.accessPermissions?.[permission.key] ? 'text-gray-800' : 'text-gray-400'}`}>
                                {permission.label}
                              </span>
                            </div>
                            {viewingSubscription.accessPermissions?.[permission.key] && (
                              <span className="text-xs font-bold px-2 py-1 bg-indigo-100 text-indigo-700 rounded">
                                {viewingSubscription.userLimits?.[permission.key] || 0} Users
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  {(viewingSubscription.createdAt || viewingSubscription._id) && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-800 mb-3">Additional Information</h4>
                      <div className="space-y-2">

                        {viewingSubscription.createdAt && (
                          <div>
                            <span className="text-sm font-medium text-gray-600">Created Date:</span>
                            <p className="text-sm text-gray-800">{new Date(viewingSubscription.createdAt).toLocaleDateString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="mt-8 flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={closeViewModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[10000]">
            <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-md text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Delete Subscription Plan</h2>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this subscription plan? This action cannot be undone.</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={cancelDelete}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-xl hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition"
                >
                  Delete Plan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Alert Box */}
        {showAlert && (
          <AlertBox
            message={showAlert}
            onClose={() => setShowAlert(null)}
          />
        )}
      </div>
    </div>
  );
};

export default Subscription;

